import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {SectionCard} from '@/components/section-card'
import {Fact} from '@/components/fact'
import type {FactorPlan, Periodicidad, Profesional} from '@/hooks/bateria/use-bateria-informe'
import {formatDateOnly} from '@/lib/risk'

/** Bloque 9 (§P7). Los protocolos van solo por nombre: la fuente oficial no entregó su contenido. */
export function PeriodicidadYProtocolos({
  periodicidad,
  articulo3,
  protocoloGeneral,
  factoresConProtocolo,
}: {
  periodicidad: Periodicidad
  articulo3: string[]
  protocoloGeneral: string
  factoresConProtocolo: FactorPlan[]
}) {
  const {t} = useTranslation()
  const protocolos = useMemo(() => {
    const set = new Set<string>([protocoloGeneral])
    for (const f of factoresConProtocolo) set.add(f.protocolo)
    return [...set]
  }, [protocoloGeneral, factoresConProtocolo])

  return (
    <SectionCard title={t('admin.bateria.informe.periodicidad.title')} testId="informe-periodicidad">
      <div className="space-y-4">
        <div>
          <p className="text-sm text-foreground" data-testid="informe-periodicidad-conclusion">
            {t('admin.bateria.informe.periodicidad.conclusion', {
              periodo:
                periodicidad.meses === 12
                  ? t('admin.bateria.informe.periodicidad.anual')
                  : t('admin.bateria.informe.periodicidad.bienal'),
              motivo: periodicidad.motivo,
            })}
          </p>
          {periodicidad.recomendacion_inci && (
            <p
              className="mt-2 rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground"
              data-testid="informe-periodicidad-recomendacion">
              {periodicidad.recomendacion_inci}
            </p>
          )}
        </div>

        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-foreground">
            {t('admin.bateria.informe.periodicidad.citaTitle')}
          </summary>
          <div className="mt-2 space-y-2 border-l-2 border-border pl-3 text-xs text-muted-foreground italic">
            {articulo3.map((parrafo, i) => (
              <p key={i}>{parrafo}</p>
            ))}
          </div>
        </details>

        <div>
          <p className="text-sm font-medium text-foreground">
            {t('admin.bateria.informe.periodicidad.protocolosTitle')}
          </p>
          <ul
            className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-muted-foreground"
            data-testid="informe-protocolos">
            {protocolos.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      </div>
    </SectionCard>
  )
}

/** Bloque 10 (§P7): el mismo bloque de firma que imprime el DOCX. */
export function Firma({profesional}: {profesional: Profesional}) {
  const {t} = useTranslation()
  return (
    <SectionCard title={t('admin.bateria.informe.firma.title')} testId="informe-firma">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Fact label={t('admin.bateria.informe.ficha.psicologa')}>{profesional.nombre}</Fact>
        <Fact label={t('admin.bateria.informe.firma.titulo')}>
          {profesional.titulo ?? t('admin.bateria.informe.firma.tituloDefault')}
        </Fact>
        {profesional.especializacion && (
          <Fact label={t('admin.bateria.informe.firma.especializacion')}>{profesional.especializacion}</Fact>
        )}
        <Fact label={t('admin.bateria.informe.ficha.licencia')}>
          {profesional.licencia
            ? t('admin.bateria.informe.ficha.licenciaValue', {
                licencia: profesional.licencia,
                fecha: profesional.licencia_fecha ? formatDateOnly(profesional.licencia_fecha) : '',
              }).trim()
            : t('admin.bateria.informe.ficha.sinLicencia')}
        </Fact>
      </div>
    </SectionCard>
  )
}
